import cn from 'classnames'
import { Dropdown } from 'legacy/lut/library/dropdown'
import { FC, useCallback } from 'react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { getUrlWithParams } from 'core-components/helpers/url'
import { Loader } from 'core-components/library/loader'
import { Static } from 'core-components/types'
import MeasureController from 'core/analytics/MeasureController'
import { useMeasureController } from 'core/analytics/useMeasureController'
import { Crud } from 'core/types/Crud'

import { SimpleField } from '../fields/SimpleField'
import { HintLegend } from '../HintLegend'

import { IPartnerGroup } from './IPartnerGroup'
import { PartnerGroupApi, PartnerType } from './PartnerGroupApi'

import styles from './PartnerGroup.module.scss'

interface IPartnerGroupProps {
  partnerId: string
  fYear: number
  partnerType: PartnerType
  metrics: MeasureController
  className?: string
}

export const PartnerGroup: FC<IPartnerGroupProps> = ({
  partnerId,
  fYear,
  partnerType,
  metrics,
  className,
}) => {
  const [currentPartner, setCurrentPartner] = useState<IPartnerGroup | IObjectAny>({})
  const [isLoaded, setIsLoaded] = useState(false)
  const [partnerGroup, setPartnerGroup] = useState<IPartnerGroup[]>([])
  const { startLoadData, stopLoadData } = useMeasureController(metrics)
  const navigate = useNavigate()
  const [partnerGroupCrud, setPartnerGroupCrud] = useState(
    new Crud({
      fetcher: new Static(partnerGroup),
      keyField: 'EntryId',
      defaultPaging: { page: 0, pageSize: partnerGroup.length },
      defaultSort: [{ field: 'PartnerId', dir: 'asc' }],
    })
  )

  const isDistributor = partnerType === 'Distributor'
  const translateKey = `jbp-${partnerType.toLocaleLowerCase()}-group`

  const changePartner = (partnerId: string) => {
    if (partnerId) {
      navigate(getUrlWithParams({ pid: partnerId }))
    }
  }

  const onPartnerChange = (entryId: string) => {
    if (entryId) {
      const id = Number(entryId)
      const selectedPartner = partnerGroup.filter((el) => el.EntryId === id)
      if (selectedPartner.length) {
        changePartner(selectedPartner[0].PartnerId)
      }
    }
  }

  const onChangeToRegionalPartner = () => {
    const regionalPartner = partnerGroup.filter((el) => el.EntryId === -1)
    if (regionalPartner.length) {
      changePartner(regionalPartner[0].PartnerId)
    }
  }

  const fetchPartnerGroup = useCallback(async () => {
    setIsLoaded(false)
    startLoadData()

    setPartnerGroup([])
    setCurrentPartner({})

    const result = await PartnerGroupApi.fetchPartnerGroup(partnerId, fYear, partnerType)
    const rows: IPartnerGroup[] = (result && result['Rows']) || []
    if (rows.length) {
      setPartnerGroup(rows)
      const currentPartners = rows.filter((el) => el.PartnerId === partnerId)
      const currentPartner = currentPartners.length ? currentPartners[0] : {}
      setCurrentPartner(currentPartner)
    }

    stopLoadData()
    setIsLoaded(true)
  }, [startLoadData, stopLoadData, partnerId, fYear, partnerType])

  useEffect(() => {
    if (partnerId && fYear && partnerType) {
      fetchPartnerGroup()
    }
  }, [partnerId, fYear, partnerType, fetchPartnerGroup])

  useEffect(() => {
    setPartnerGroupCrud(
      new Crud({
        fetcher: new Static(partnerGroup),
        keyField: 'EntryId',
        defaultPaging: { page: 0, pageSize: partnerGroup.length },
        defaultSort: [{ field: 'PartnerId', dir: 'asc' }],
      })
    )
  }, [partnerGroup])

  const isGroupEmptyOrContainsOnlyCurrentPartner = !partnerGroup.some(
    (el) => el.PartnerId !== partnerId
  )

  if (!isLoaded) {
    return <Loader />
  } else if (isGroupEmptyOrContainsOnlyCurrentPartner) {
    return null
  }

  return (
    <div className={cn(styles.root, className)}>
      <fieldset className={styles.fieldset}>
        <HintLegend className={styles.legend} text={translateKey} />
        <div className={styles.group}>
          <div className={styles.left}>
            <div className={cn(styles.title, styles.fieldRow, styles.link)}>
              <span onClick={() => onChangeToRegionalPartner()}>{currentPartner.GroupName}</span>
            </div>
            <div className={styles.fieldPlace}>
              <SimpleField
                className={styles.col}
                item={currentPartner}
                value={currentPartner.Country}
                field={'Country'}
                label={`${translateKey}-country`}
                size={'sm'}
                allowZero
                translate
              />
            </div>
          </div>
          {isDistributor && (
            <div className={styles.center}>
              <div className={styles.fieldRow}>
                <SimpleField
                  className={styles.col}
                  item={currentPartner}
                  value={currentPartner.District}
                  field={'District'}
                  label={`${translateKey}-district`}
                  size={'sm'}
                  allowZero
                  translate
                />
              </div>
              <div>
                <SimpleField
                  className={styles.col}
                  item={currentPartner}
                  value={currentPartner.Cluster}
                  field={'Cluster'}
                  label={`${translateKey}-cluster`}
                  size={'sm'}
                  allowZero
                  translate
                />
              </div>
            </div>
          )}
          <div className={styles.right}>
            <div>
              <Dropdown
                caption={'PartnerName'}
                source={partnerGroupCrud}
                value={currentPartner.EntryId}
                translation={`${translateKey}-partner`}
                classes={{
                  controlClasses: {
                    popPaper: styles.dropdownPaper,
                  },
                }}
                margin={'sm'}
                onChange={onPartnerChange}
                renderFunction={(item) => (
                  <div className={styles.dropdownContainer}>
                    <div className={styles.title}>{item.PartnerName}</div>
                    <div>
                      ID:
                      <span className={styles.fieldText}>{item.PartnerId}</span>
                    </div>
                    <div className={styles.subField}>
                      <span>{item.Country}</span>
                      <span className={cn(styles.fieldText, styles.subFieldComment)}>
                        ({item.PartnerLevel})
                      </span>
                    </div>
                  </div>
                )}
              />
            </div>
          </div>
        </div>
      </fieldset>
    </div>
  )
}

PartnerGroup.displayName = 'PartnerGroup'
